import { FC } from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';
import useLocales from '../../../hooks/useLocales';
import useFormattedText from '../../../hooks/useFormattedText';
import { InAppNotificationItem } from '../../../@types/notification.types';
import { getNotificationIcon, formatNotificationTimestamp } from '../../../utils/notificationUtils';

interface NotificationRowProps {
    item: InAppNotificationItem;
    onPress: (item: InAppNotificationItem) => void;
    /** Compact styling for the header popover; full-width row otherwise. */
    dense?: boolean;
}

const NotificationRow: FC<NotificationRowProps> = ({ item, onPress, dense }) => {
    const { translate } = useLocales();
    const { segments, accessibleText, mapStyleToFont } = useFormattedText({ text: item.body });
    const label = item.isRead
        ? accessibleText
        : `${translate('layouts.notificationsPopover.unread')}. ${accessibleText}`;

    return (
        <ButtonBase
            onClick={() => onPress(item)}
            aria-label={label}
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                textAlign: 'left',
                gap: 1.25,
                p: dense ? 1.25 : 1.5,
                borderRadius: dense ? '12px' : 1.5,
                bgcolor: item.isRead ? 'transparent' : 'primary.lightest',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    minWidth: 48,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                }}
            >
                <Iconify icon={getNotificationIcon(item.type)} width={24} height={24} />
            </Box>
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {segments.map((segment, idx) => (
                        <Box key={idx} component="span" sx={mapStyleToFont(segment)}>
                            {segment.isBreak ? <br /> : segment.text}
                        </Box>
                    ))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatNotificationTimestamp(item.createdAt, translate)}
                </Typography>
            </Stack>
        </ButtonBase>
    );
};

export default NotificationRow;
